import React, { useCallback, useEffect, useState } from 'react';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import { managedFarmPageInfo, pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MonthNavigator } from './components/MonthNavigator';
import { TaskCard } from './components/TaskCard';
import { Icon } from '../../../components/common/icon/Icon';
import { EmptyState } from '../../../components/common/empty/EmptyState';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { monthNavigatorSlice } from '../../../store/monthNavigatorSlice';
import { getTaskListByDate, getTaskListFromStorage } from '../../../utils/manager/managedFarmTaskManager.util';
import { IManagedFarmTask } from '../../../interfaces/ManagedFarm.interfaces';
import { Helmet } from 'react-helmet';
import { ampli } from '../../../ampli';
import ReactGA from 'react-ga4';

export const ManagedFarmMyTaskPage = () => {
  const navigate = useNavigate();

  const { date } = useAppSelector((state) => state.monthNavigator);
  const { resetState } = monthNavigatorSlice.actions;
  const dispatch = useAppDispatch();
  const [filteredTaskList, setFilteredTaskList] = useState<IManagedFarmTask[]>([]);

  const updateFilteredTaskList = useCallback(async () => {
    const taskList = await getTaskListByDate(new Date(date));
    setFilteredTaskList(taskList);
  }, [date, setFilteredTaskList]);

  const goToSearchFieldPage = async (buttonType: 'fab' | 'emptySection') => {
    const savedTaskList = await getTaskListFromStorage();

    if (savedTaskList) {
      if (buttonType === 'fab') {
        ampli.pvmClickEnrollManagedFarmTaskFab({
          total_enrolled_tasks: savedTaskList.length,
        });
        ReactGA.event('pvm_click_enroll_managed_farm_task_fab', {
          total_enrolled_tasks: savedTaskList.length,
        });
      }

      if (buttonType === 'emptySection') {
        ampli.pvmClickEnrollmanagedFarmTaskButtonOnEmptySection({
          total_enrolled_tasks: savedTaskList.length,
        });
      }
    }
    navigate(managedFarmPageInfo.searchFieldPage.path);
  };

  const resetYearAndMonth = () => {
    dispatch(resetState());
  };

  useLayout({
    fixedHeader: (
      <MainHead
        headerTitle={'전체 작업관리'}
        backButton={{ isShow: true, directlyPassedURL: pageInfo.homePage.path, onClick: resetYearAndMonth }}
      />
    ),
    fixedFooter: (
      <FixedButton onClick={() => goToSearchFieldPage('fab')}>
        <Icon name={'Plus2'} width={20} height={20} />
        <FixedButtonText>{'작업 등록하기'}</FixedButtonText>
      </FixedButton>
    ),
    fixedHeaderHeight: 48,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  useEffect(() => {
    const loadTaskList = async () => {
      try {
        await updateFilteredTaskList();
      } catch (error) {
        console.error('태스크 리스트 로드 실패:', error);
      }
    };

    loadTaskList();
  }, [date, updateFilteredTaskList]);

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.myTaskPage.title}</title>
      </Helmet>
      <Container>
        <TopSection>
          <ManagedFarmDescriptionBackground>
            <ManagedFarmDescriptionInnerContainer>
              <ManagedFarmDescriptionTitle>{'📝 대행반장이 뭐예요?'}</ManagedFarmDescriptionTitle>
              <ManagedFarmDescription>
                {'작업만 등록하면, 논 평수 측정부터 고객 연락까지 한 번에 관리할 수 있는 기능이예요!'}
              </ManagedFarmDescription>
            </ManagedFarmDescriptionInnerContainer>
          </ManagedFarmDescriptionBackground>
        </TopSection>

        <BottomSection>
          <MonthNavigator />
          {filteredTaskList && filteredTaskList.length === 0 && (
            <EmptyState
              title={'아직 등록된 작업이 없어요.'}
              buttonText={'작업 등록하러 가기'}
              onClick={() => goToSearchFieldPage('emptySection')}
            />
          )}

          {filteredTaskList &&
            filteredTaskList.length > 0 &&
            filteredTaskList.map((task) => (
              <TaskCard key={task.id} task={task} onStatusUpdate={updateFilteredTaskList} />
            ))}
        </BottomSection>
      </Container>
    </>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 48px);

  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  background: var(--Light-Scheme-onochrome-grey10, #f2f2f7);
`;

const TopSection = styled.div`
  padding: 32px 16px 0px 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const ManagedFarmDescriptionBackground = styled.div`
  padding: 24px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const ManagedFarmDescriptionTitle = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */

  align-self: stretch;
`;

const ManagedFarmDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const ManagedFarmDescriptionInnerContainer = styled.div`
  padding: 0px 6px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
`;

const BottomSection = styled.div`
  padding: 32px 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const FixedButton = styled.button`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;

  position: fixed;
  right: 16px;
  bottom: 24px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const FixedButtonText = styled.span`
  display: -webkit-box;
  max-height: 42px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-overflow: ellipsis;

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */
`;
